import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { isEmpty, uniqueId, findIndex, omit, find } from 'lodash/fp';
import get from 'lodash/get';
import {
  ORDER,
  COLUMNS,
  TYPES,
  routes as ROUTES,
  TYPE_ICONS,
} from 'constants/config';
import ContentItem from 'components/shared/ContentItem';
import LoadableState from 'components/shared/loadable';
import TextArea from 'components/shared/debouncedTextArea';
import { Icon, Spinner } from 'ui';
import { pluralize } from 'utils/string_utils';
import request from 'utils/request_ajax';
import ScrollableContainer from 'components/shared/scrollable';
import PickerBlankSlate from './PickerBlankSlate';

class ContentPicker extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      viewLayout: 'card',
      loading: true,
      success: true,
      data: [],
      page: 1,
      query: '',
      error: null,
      sort: {
        order: ORDER.DESC,
        type: COLUMNS.LAST_MODIFIED,
      },
      filter: {
        type: ContentPicker.contentTypes[props.type],
      },
    };
    this.handleQueryChange = this.handleQueryChange.bind(this);
    this.handleLayoutChange = this.handleLayoutChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleShowMoreBtnClick = this.handleShowMoreBtnClick.bind(this);
  }

  static sortOptions = [
    {
      order: ORDER.DESC,
      type: COLUMNS.LAST_MODIFIED,
      label: 'Recently Updated',
    },
    { order: ORDER.ASC, type: COLUMNS.TITLE, label: 'Name A-Z' },
    { order: ORDER.DESC, type: COLUMNS.TITLE, label: 'Name Z-A' },
  ];

  static contentTypes = {
    [TYPES.LESSON]: [TYPES.LESSON, TYPES.RESOURCE],
    [TYPES.PATH]: [TYPES.PATH],
  };

  get selectedSortIndex() {
    return findIndex(this.state.sort, ContentPicker.sortOptions);
  }

  get pagination() {
    const { data, perPage, totalResults } = this.state;
    return {
      hasMore: data.length < totalResults,
      moreCount: Math.min(perPage, totalResults - data.length),
    };
  }

  get contentsRoute() {
    if (this.props.type === TYPES.CERTIFICATION) {
      return ROUTES.PATH_ADD_CONTENT_CERTIFICATION;
    }
    if (this.props.pathId) {
      return ROUTES.PATH_ADD_OWN_CONTENT(this.props.pathId);
    }
    return ROUTES.PATH_ADD_CONTENT;
  }

  componentDidMount() {
    this.fetchData(this.state.page);
  }

  handleLayoutChange({ target }) {
    this.setState({ viewLayout: target.value });
  }

  handleQueryChange(value) {
    this.setState({ query: value }, this.fetchData);
  }

  handleSortChange({ target }) {
    this.setState(
      {
        sort: omit('label', ContentPicker.sortOptions[target.value]),
      },
      this.fetchData,
    );
  }

  handleShowMoreBtnClick() {
    this.fetchData(this.state.page + 1);
  }

  fetchData(page) {
    this.setState({ loading: true }, () => {
      const { data: currentData, query, sort, filter } = this.state;
      request
        .get(this.contentsRoute, { page, query, sort, filter })
        .then(({ data, ...res }) => {
          if (this.pickerNode) {
            this.setState({
              loading: false,
              success: true,
              data: page ? currentData.concat(data) : data,
              ...res,
            });

            if (!page) {
              $(this.pickerNode).find('.ScrollableContainer').scrollTop(0);
            }
          }
        })
        .fail(() => {
          this.setState({ loading: false, success: false });
        });
    });
  }

  selectContent(item) {
    const itemAlreadySelected = find({ id: item.id, type: item.type })(
      this.props.contents,
    );
    const isSelectingSelf = item.id === this.props.id;

    if (itemAlreadySelected && !isSelectingSelf) {
      this.setState({ error: 'Content is already selected' });
    } else {
      this.props.onChange(item);
    }
  }

  renderContent() {
    return this.state.data.map(item => {
      const isPath = item.type === TYPES.PATH;
      const isCert = get(item, 'meta.certification.iconName', false);
      let iconName = isPath && 'path';
      if (isCert) {
        iconName = TYPE_ICONS.Certification;
      }
      return (
        <ContentItem
          {...item}
          key={item.id}
          tags={item.meta.tags}
          layout={this.state.viewLayout}
          iconName={iconName}
          onClick={() => this.selectContent(item)}
          count={item.meta.stepsCount}
          countLabel={isPath && pluralize(item.meta.stepsCount, 'Lesson')}
        />
      );
    });
  }

  render() {
    const isCardLayout = this.state.viewLayout === 'card';
    const isListLayout = this.state.viewLayout === 'list';
    const isLoading = this.state.loading;
    const loadedSuccess = !isLoading && this.state.success;
    const loadedFailure = !isLoading && !this.state.success;
    const { hasMore, moreCount } = this.pagination;

    const dataViewClassName = classNames('PathContentPicker-dataView', {
      'PathContentPicker-dataView--card': isCardLayout,
      'PathContentPicker-dataView--list': isListLayout,
    });

    const layoutButtonClassName = 'u-resetBtn PathContentPicker-layoutBtn';
    const cardLayoutButtonClass = classNames(layoutButtonClassName, {
      'is-active': isCardLayout,
    });
    const listLayoutButtonClass = classNames(layoutButtonClassName, {
      'is-active': isListLayout,
    });

    return (
      <div
        className="PathContent PathContentPicker"
        ref={n => {
          this.pickerNode = n;
        }}
      >
        <div className="PathContent-header">
          <div className="PathContent-title f-smooth">{this.props.title}</div>
          <button
            type="button"
            className="u-linkBtn u-linkBtn--dark td-u"
            onClick={this.props.onCancel}
            children="Cancel"
          />
        </div>
        <div className="PathContent-body">
          <TextArea
            onChange={this.handleQueryChange}
            className="AssignModal-searchInput"
            placeholder="Search content"
            singleLine
            maxLength="255"
          />
          <div className="d-f jc-sb ai-c mb-12">
            <div className="PathContentPicker-layoutOptions">
              <button
                className={cardLayoutButtonClass}
                onClick={this.handleLayoutChange}
                value="card"
                type="button"
              >
                <Icon name="thumb" stabilize={false} />
              </button>
              <button
                className={listLayoutButtonClass}
                onClick={this.handleLayoutChange}
                value="list"
                type="button"
              >
                <Icon name="thumb-list" stabilize={false} />
              </button>
            </div>
            <div className="AssignModal-sortButton">
              <select
                className="NewButton NewButton--white"
                onChange={this.handleSortChange}
                value={this.selectedSortIndex}
              >
                {ContentPicker.sortOptions.map((option, i) => (
                  <option
                    key={uniqueId('option')}
                    value={i}
                    children={option.label}
                  />
                ))}
              </select>
              <div className="AssignModal-sortIcon">
                <Icon name="sort" />
              </div>
            </div>
          </div>
          {this.state.error && (
            <div className="CommentForm-errorMessage mb-12">
              {this.state.error}
            </div>
          )}
          {isLoading && isEmpty(this.state.data) && (
            <ScrollableContainer className="d-f jc-c ai-c" maxHeight={null}>
              <div>
                <Spinner />
                <span className="ml-8 c-brand-gray-darker">Loading...</span>
              </div>
            </ScrollableContainer>
          )}
          {loadedFailure && (
            <PickerBlankSlate
              icon="danger"
              description="Something went wrong. Please try again."
            />
          )}
          {loadedSuccess && isEmpty(this.state.data) && (
            <PickerBlankSlate
              icon="filter"
              description="We couldn't find any Learning Content"
            />
          )}
          {!isEmpty(this.state.data) && (
            <ScrollableContainer maxHeight={null}>
              <LoadableState loading={isLoading}>
                <div
                  className={dataViewClassName}
                  children={this.renderContent()}
                />
                {hasMore && (
                  <button
                    type="button"
                    className="AssignModal-showMoreButton"
                    onClick={this.handleShowMoreBtnClick}
                  >
                    <Icon name="add" />
                    Show {moreCount} More
                  </button>
                )}
              </LoadableState>
            </ScrollableContainer>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const storePath = ownProps.storeLocation
    ? ownProps.storeLocation.storePath
    : 'pathBuilder';
  const contentArray = ownProps.storeLocation
    ? ownProps.storeLocation.contentArray
    : 'contents';
  const contents = state[storePath][contentArray];

  return {
    contents,
  };
}

export default connect(mapStateToProps)(ContentPicker);
